<template>
  <v-card flat class="kpi-card">
    <div :class="['kpi-accent', `kpi-accent--${type}`]"></div>
    <div class="kpi-body">

      <!-- Top: label / value / icon -->
      <div class="kpi-top">
        <div>
          <span class="kpi-label">{{ config.label }}</span>
          <div class="kpi-value">{{ kpi.value }}</div>
          <span class="kpi-period">Période sélectionnée</span>
        </div>
        <div :class="['kpi-icon', `kpi-icon--${type}`]">
          <v-icon color="white" size="24">{{ config.icon }}</v-icon>
        </div>
      </div>

      <!-- Objective box -->
      <div class="kpi-objective-box">
        <div class="kpi-obj-header">
          <span class="kpi-obj-title">
            <v-icon x-small class="mr-1" :color="config.color">
              {{ view === 'month' ? icons.mdiCalendarMonth : icons.mdiFlagCheckered }}
            </v-icon>
            {{ objTitle }}
          </span>
          <span class="kpi-obj-value">Obj: {{ kpi.objective }}</span>
        </div>
        <v-progress-linear
          :value="Math.min(kpi.progress, 100)"
          height="6"
          rounded
          :color="config.color"
          :background-color="config.bgColor"
          class="kpi-progress"
        ></v-progress-linear>
        <div class="kpi-obj-footer">
          <div>
            <span class="kpi-proj-label">{{ config.projectionLabel }}</span>
            <span :class="['kpi-proj-value', `kpi-proj-value--${type}`]">{{ type === 'rpm' ? kpi.current : kpi.projection }}</span>
          </div>
          <v-chip
            :style="ratioChipStyle"
            small
            label
            class="kpi-ratio"
          >
            <v-icon x-small left>{{ ratioIcon }}</v-icon>
            {{ kpi.ratio }}%
          </v-chip>
        </div>
      </div>

      <!-- Compare toggle -->
      <button class="kpi-compare-toggle" @click="$emit('toggle-compare')">
        {{ expanded ? 'Masquer' : 'Afficher' }} comparaisons
        <v-icon x-small class="ml-1">{{ expanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
      </button>

      <!-- Compare details -->
      <transition name="slide">
        <div v-if="expanded" class="kpi-compare-row">
          <div class="kpi-compare-item">
            <span class="kpi-compare-label">{{ primaryCompareLabel }}</span>
            <div class="kpi-compare-values">
              <span>{{ formattedPrev }}</span>
              <span :class="['kpi-trend', kpi.comparison.prevTrend]">
                ({{ formattedPrevPercent }})
              </span>
            </div>
          </div>
          <div v-if="view !== 'year'" class="kpi-compare-item kpi-compare-item--right">
            <span class="kpi-compare-label">{{ secondaryCompareLabel }}</span>
            <div class="kpi-compare-values">
              <span>{{ formattedYear }}</span>
              <span :class="['kpi-trend', kpi.comparison.yearTrend]">
                ({{ formattedYearPercent }})
              </span>
            </div>
          </div>
        </div>
      </transition>

    </div>
  </v-card>
</template>

<script>
import {
  mdiCurrencyEur,
  mdiAccountGroup,
  mdiSpeedometer,
  mdiCalendarMonth,
  mdiFlagCheckered,
  mdiArrowUp,
  mdiArrowDown,
  mdiChevronUp,
  mdiChevronDown,
  mdiCheck,
} from '@mdi/js'
import { computed } from '@vue/composition-api'
import { formatDateRangeToken } from '@/utils/dates'
import { formatCurrencyCompact, formatNumberCompact, formatPercent } from '@/utils/formatting'
import {
  RMR_CA,
  RMR_CA_TINT,
  RMR_RPM,
  RMR_RPM_TINT,
  RMR_SESSIONS,
  RMR_SESSIONS_TINT,
} from '@/styles/tokens'

const COMPONENT_NAME = 'DashboardV2KpiCard'

const formatKpiValue = (type, val) => {
  if (val == null) return '--'
  if (type === 'ca') return formatCurrencyCompact(val)
  if (type === 'sessions') return formatNumberCompact(val)
  return `${Number(val).toFixed(2)}€`
}

const formatKpiPercent = (val) => (val == null ? '--' : formatPercent(val, 1))

const KPI_CONFIGS = {
  ca: {
    label: "Chiffre d'Affaires",
    icon: mdiCurrencyEur,
    color: RMR_CA,
    bgColor: RMR_CA_TINT,
    projectionLabel: 'Projection',
  },
  sessions: {
    label: 'Sessions',
    icon: mdiAccountGroup,
    color: RMR_SESSIONS,
    bgColor: RMR_SESSIONS_TINT,
    projectionLabel: 'Projection',
  },
  rpm: {
    label: 'RPM Global',
    icon: mdiSpeedometer,
    color: RMR_RPM,
    bgColor: RMR_RPM_TINT,
    projectionLabel: 'Moyenne actuelle',
  },
}

const icons = {
  mdiCalendarMonth,
  mdiFlagCheckered,
  mdiArrowUp,
  mdiArrowDown,
  mdiChevronUp,
  mdiChevronDown,
  mdiCheck,
}

export default {
  name: COMPONENT_NAME,
  props: {
    type: {
      type: String,
      required: true,
      validator: (v) => ['ca', 'sessions', 'rpm'].includes(v),
    },
    kpi: {
      type: Object,
      required: true,
    },
    view: {
      type: String,
      default: 'month',
    },
    dateFrom: {
      type: String,
      default: '',
    },
    dateTo: {
      type: String,
      default: '',
    },
    currentMonthLabel: {
      type: String,
      default: '',
    },
    currentYear: {
      type: [String, Number],
      default: '',
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // => COMPUTED
    const config = computed(() => KPI_CONFIGS[props.type])

    const rangeLabel = computed(() => {
      const label = formatDateRangeToken(props.dateFrom, props.dateTo)
      return label || 'Période sélectionnée'
    })

    const objTitle = computed(() => {
      if (props.type === 'rpm') {
        if (props.view === 'range') return `Moy. ${rangeLabel.value}`
        return props.view === 'month' ? `Moy. ${props.currentMonthLabel}` : 'Moy. Année'
      }
      if (props.view === 'range') return rangeLabel.value
      return props.view === 'month' ? props.currentMonthLabel : `Année ${props.currentYear}`
    })

    const formattedPrev = computed(() => formatKpiValue(props.type, props.kpi.comparison?.prev))
    const formattedPrevPercent = computed(() => formatKpiPercent(props.kpi.comparison?.prevPercent))
    const formattedYear = computed(() => formatKpiValue(props.type, props.kpi.comparison?.year))
    const formattedYearPercent = computed(() => formatKpiPercent(props.kpi.comparison?.yearPercent))

    const ratioIcon = computed(() =>
      props.kpi.ratio >= 100 ? icons.mdiArrowUp : icons.mdiArrowDown
    )

    const ratioChipStyle = computed(() =>
      props.kpi.ratio >= 100
        ? { backgroundColor: '#E8F5EA', color: '#4A7C59' }
        : { backgroundColor: '#FBEAEA', color: '#B05656' }
    )

    const comparePrimaryLabel = computed(() => {
      if (props.view === 'month') return 'vs M-1'
      if (props.view === 'range') return 'vs Période préc.'
      return 'vs Année N-1'
    })

    const primaryCompareLabel = computed(() =>
      props.kpi?.comparison?.prevLabel || comparePrimaryLabel.value
    )

    const secondaryCompareLabel = computed(() =>
      props.kpi?.comparison?.yearLabel || 'vs N-1'
    )

    // => DATA
    return {
      icons,
      config,
      objTitle,
      formattedPrev,
      formattedPrevPercent,
      formattedYear,
      formattedYearPercent,
      ratioIcon,
      ratioChipStyle,
      primaryCompareLabel,
      secondaryCompareLabel,
    }
  },
}
</script>

<style lang="scss" scoped>
.kpi-card {
  border-radius: $rmr-radius-2xl !important;
  overflow: hidden;
  box-shadow: $rmr-shadow-card !important;
  transition: box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    box-shadow: $rmr-shadow-card-hover !important;
    transform: translateY(-2px);
  }
}

.kpi-accent {
  height: 4px;

  &--ca       { background: linear-gradient(90deg, $rmr-ca, $rmr-ca-light); }
  &--sessions { background: linear-gradient(90deg, $rmr-sessions-dark, $rmr-sessions); }
  &--rpm      { background: linear-gradient(90deg, $rmr-rpm-dark, $rmr-rpm); }
}

.kpi-body {
  padding: 22px $rmr-space-xl 18px;
}

.kpi-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.kpi-label {
  font-size: $rmr-fs-sm;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: $rmr-text-subtle;
}

.kpi-value {
  font-size: $rmr-fs-kpi;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -1.5px;
  color: $rmr-text-primary;
  margin: 8px 0 4px;
}

.kpi-period {
  font-size: $rmr-fs-sm;
  color: $rmr-text-faint;
  font-weight: 500;
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: $rmr-radius-xl;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--ca       { background: linear-gradient(135deg, $rmr-ca, $rmr-ca-light); }
  &--sessions { background: linear-gradient(135deg, $rmr-sessions-dark, $rmr-sessions); }
  &--rpm      { background: linear-gradient(135deg, $rmr-rpm-dark, $rmr-rpm); }
}

.kpi-objective-box {
  background: $rmr-surface-2;
  border-radius: $rmr-radius-lg;
  padding: 14px $rmr-space-md;
  border: 1px solid $rmr-border;
}

.kpi-obj-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.kpi-obj-title {
  font-size: $rmr-fs-sm;
  font-weight: 800;
  text-transform: uppercase;
  color: $rmr-text-muted;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.kpi-obj-value {
  font-size: $rmr-fs-sm;
  font-weight: 600;
  color: $rmr-text-subtle;
}

.kpi-progress {
  border-radius: $rmr-radius-pill !important;
  margin: 6px 0;
}

.kpi-obj-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 10px;
}

.kpi-proj-label {
  display: block;
  font-size: $rmr-fs-xs;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-text-subtle;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.kpi-proj-value {
  font-size: $rmr-fs-proj;
  font-weight: 800;
  line-height: 1;

  &--ca       { color: $rmr-ca; }
  &--sessions { color: $rmr-sessions; }
  &--rpm      { color: $rmr-rpm; }
}

.kpi-ratio {
  font-weight: 700 !important;
  font-size: $rmr-fs-md !important;
  height: 28px !important;
}

.kpi-compare-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: $rmr-fs-sm;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-text-subtle;
  letter-spacing: 0.5px;
  cursor: pointer;
  padding: 12px 0 4px;
  margin-top: $rmr-space-sm;
  border: none;
  background: none;
  border-top: 1px solid $rmr-border;
  transition: color 0.2s;

  &:hover { color: $rmr-text-secondary; }
}

.kpi-compare-row {
  display: flex;
  justify-content: space-between;
  gap: $rmr-space-md;
  padding-top: $rmr-space-sm;
}

.kpi-compare-item {
  display: flex;
  flex-direction: column;

  &--right { align-items: flex-end; }
}

.kpi-compare-label {
  font-size: $rmr-fs-xs;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-text-faint;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.kpi-compare-values {
  display: flex;
  align-items: center;
  gap: $rmr-space-xs;
  font-size: $rmr-fs-md;
  font-weight: 600;
  color: $rmr-text-muted;
}

.kpi-trend {
  font-weight: 800;
  font-size: $rmr-fs-base;

  &.up   { color: $rmr-success; }
  &.down { color: $rmr-danger; }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 80px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
}

@media (max-width: 960px) {
  .kpi-value { font-size: 30px; }
}
</style>
